import { useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@volca/ui/components/ui/dialog";
import { Textarea } from "@volca/ui/components/ui/textarea";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "@volca/ui/components/ui/toggle-group";

export interface FeedbackReason {
  value: string;
  label: string;
}

/**
 * Why a reviewer pushed back: one reason, and a note if they have one. Used
 * for a thumbs down and for dismissing a finding, which ask the same thing.
 */
export const FeedbackDialog = ({
  open,
  onOpenChange,
  title,
  reasons,
  submitLabel,
  pending,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  reasons: FeedbackReason[];
  submitLabel: string;
  pending?: boolean;
  onSubmit: (feedback: { reason: string; note: string }) => void;
}) => {
  const [reason, setReason] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const close = (next: boolean) => {
    if (!next) {
      setReason(null);
      setNote("");
    }
    onOpenChange(next);
  };
  const submit = () => reason && onSubmit({ reason, note: note.trim() });
  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <ToggleGroup
          aria-label="Reason"
          value={reason ? [reason] : []}
          onValueChange={(next) => setReason(next[0] ?? null)}
          spacing={2}
          className="flex-wrap"
        >
          {reasons.map((item) => (
            <ToggleGroupItem
              key={item.value}
              value={item.value}
              variant="outline"
              size="sm"
              className="data-[pressed]:border-selected-line data-[pressed]:bg-selected data-[pressed]:text-brand"
            >
              {item.label}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
        <Textarea
          aria-label="Note"
          value={note}
          maxLength={2000}
          rows={3}
          placeholder="Add a note (optional)"
          className="text-base sm:text-sm"
          onChange={(event) => setNote(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
              event.preventDefault();
              submit();
            }
          }}
        />
        <DialogFooter>
          <Button variant="ghost" onClick={() => close(false)}>
            Cancel
          </Button>
          <Button
            disabled={!reason || pending}
            loading={pending}
            loadingLabel={`${submitLabel}, in progress`}
            onClick={submit}
          >
            {submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** Why a flagged record is not worth a look, in the words of what it is. */
export const findingDownReasons = (
  /** What the report's flagged rows are, lower case: "job", "invoice". */
  noun = "work",
): FeedbackReason[] => [
  { value: "not_an_issue", label: "Not an issue" },
  { value: "expected", label: `Expected for this ${noun}` },
  { value: "wrong_numbers", label: "Numbers are wrong" },
  { value: "already_known", label: "Already handled" },
  { value: "other", label: "Other" },
];

export const INSIGHT_DOWN_REASONS: FeedbackReason[] = [
  { value: "not_an_issue", label: "Not useful" },
  { value: "wrong_numbers", label: "Numbers are wrong" },
  { value: "already_known", label: "Already knew" },
  { value: "unclear", label: "Unclear" },
  { value: "other", label: "Other" },
];

/** Why a finding is explained: understood, and nothing to fix. */
export const EXPLAIN_REASONS: FeedbackReason[] = [
  { value: "expected", label: "Expected for this work" },
  { value: "not_an_issue", label: "Not an issue" },
  { value: "other", label: "Other" },
];
